/**
 * Tells the backend when a page fails in front of somebody.
 *
 * The bug that prompted this: /admin/interventions/all runs an unfiltered
 * collection-group query for a superadmin and a filtered one — needing an
 * index nobody had declared — for every school-bound admin. It worked for the
 * person who wrote it and failed for everyone else, and we found out because a
 * principal happened to say so out loud at a meeting.
 *
 * So an error that reaches a user should reach an email too. What gets sent is
 * the *route id* (`/admin/interventions/[studentId]`, never the filled-in
 * path), the viewer's role, and the error text; the function redacts document
 * paths out of the message before mailing it. No student data leaves the app.
 *
 * Cost: one callable invocation per distinct error per session, capped. The
 * function reads one document per report and emails at most once a day per
 * distinct error, so a page that fails for the whole school is one email and
 * one document, not four hundred.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '$lib/firebase';
import { page } from '$app/state';
import { userState } from './auth.svelte';
import { effectiveUser } from './effectiveUser.svelte';

export type ClientErrorKind =
	'firestore-index' | 'firestore-permission' | 'firestore' | 'render' | 'unhandled';

export interface ReportContext {
	kind?: ClientErrorKind;
	/** Overrides the current route — for a failure reported after navigating. */
	routeId?: string;
}

/**
 * Per-session limits.
 *
 * A render loop or a retry that re-fails can produce errors as fast as the
 * browser can paint. The signature set stops the same error reporting twice,
 * and the cap stops a *variety* of them from turning one bad session into a
 * few hundred function calls.
 */
const MAX_REPORTS_PER_SESSION = 8;
const reported = new Set<string>();
let sent = 0;

/** Exposed for tests; not part of the reporting contract. */
export function resetErrorReportingForTests(): void {
	reported.clear();
	sent = 0;
}

function messageOf(error: unknown): string {
	if (error instanceof Error) return error.message || error.name;
	if (typeof error === 'string') return error;
	try {
		// JSON.stringify returns undefined for undefined, a function or a symbol,
		// which would make a reporter that must never throw throw on `?? ''`.
		return JSON.stringify(error) ?? String(error);
	} catch {
		return String(error);
	}
}

function codeOf(error: unknown): string | undefined {
	if (error && typeof error === 'object' && 'code' in error) {
		const code = (error as { code?: unknown }).code;
		if (typeof code === 'string') return code;
	}
	return undefined;
}

/**
 * What kind of failure this is.
 *
 * Firestore reports a missing index as `failed-precondition` with the
 * create-an-index URL in the message — the one error where the fix is sitting
 * right there in the text, which is why it gets its own kind and its own
 * paragraph in the email.
 */
export function classify(error: unknown): ClientErrorKind {
	const code = codeOf(error);
	const message = messageOf(error).toLowerCase();
	if (code === 'permission-denied' || message.includes('missing or insufficient permissions')) {
		return 'firestore-permission';
	}
	if (code === 'failed-precondition' || message.includes('requires an index')) {
		return 'firestore-index';
	}
	if (code?.includes('/') || code === 'unavailable' || code === 'resource-exhausted') {
		return 'firestore';
	}
	return 'unhandled';
}

/**
 * Group errors the way the backend will, so the session cap counts distinct
 * problems rather than distinct occurrences.
 */
function signatureOf(kind: string, routeId: string, message: string): string {
	return `${kind}|${routeId}|${message.replace(/\d+/g, '#').slice(0, 200)}`;
}

function currentRouteId(): string {
	try {
		return page.route.id ?? page.url?.pathname ?? 'unknown';
	} catch {
		// `page` is only populated inside the SvelteKit app; a report from a
		// global handler during startup has no route yet.
		return 'unknown';
	}
}

/**
 * Report an error. Never throws and never rejects: a failure to report must
 * not become a second failure on top of the one the user already has.
 */
export async function reportClientError(
	error: unknown,
	context: ReportContext = {}
): Promise<void> {
	try {
		// The callable requires auth, and `functions` is null in Storybook and
		// during SSR, where there is nothing to report to.
		if (!functions || !userState.user) return;

		const kind = context.kind ?? classify(error);
		const routeId = context.routeId ?? currentRouteId();
		const message = messageOf(error);
		const signature = signatureOf(kind, routeId, message);

		if (reported.has(signature)) return;
		if (sent >= MAX_REPORTS_PER_SESSION) return;
		reported.add(signature);
		sent += 1;

		const callable = httpsCallable(functions, 'reportClientError');
		await callable({
			kind,
			routeId,
			message,
			errorName: error instanceof Error ? error.name : undefined,
			errorCode: codeOf(error),
			stack: error instanceof Error ? error.stack : undefined,
			role: effectiveUser.adminRole ?? (userState.isAdmin ? 'admin' : 'staff'),
			impersonating: effectiveUser.isImpersonating
		});
	} catch (reportingFailure) {
		console.warn('Could not report a client error:', reportingFailure);
	}
}

/**
 * Catch what nothing else does: errors thrown outside a component's render and
 * promises nobody awaited.
 *
 * Returns a teardown function. Installed once, from the root layout.
 */
export function installGlobalErrorReporting(): () => void {
	if (typeof window === 'undefined') return () => {};

	const onError = (event: ErrorEvent) => {
		void reportClientError(event.error ?? event.message, { kind: 'unhandled' });
	};
	const onRejection = (event: PromiseRejectionEvent) => {
		void reportClientError(event.reason, {});
	};

	window.addEventListener('error', onError);
	window.addEventListener('unhandledrejection', onRejection);
	return () => {
		window.removeEventListener('error', onError);
		window.removeEventListener('unhandledrejection', onRejection);
	};
}
